import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button, Badge, Alert, Form, InputGroup } from 'react-bootstrap';
import { CenteredLoader } from '../../../components/LoadingSpinner';
import { api } from '../../../shared/api';
import { useRealtimeEvents } from '../../../shared/useRealtimeEvents';
import { useSortPreference } from '../../../shared/useSortPreference';
import Select from '../../../components/form/AppSelect';
import DateRangeField from '../../../components/form/DateRangeField';
import { useCurrency } from '../../../shared/CurrencyContext';
import { usePermissions } from '../../../shared/permissions';
import { useConfirm } from '../../../shared/ConfirmContext';
import '../../../shared/ReactSelectDarkMode.css';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PageSection from '../../../components/layout/PageSection';
import { useConflictCheck } from '../../../shared/useConflictCheck';
import './ProjectStaff.css';

const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)',
  }
});

const ProjectStaff = ({ projectId, onDirtyChange, projectStartDate, projectEndDate }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const { formatCurrency } = useCurrency();

  const queryClient = useQueryClient();
  const [staff, setStaff] = useState([]);
  const [crewMembers, setCrewMembers] = useState([]);
  const [error, setError] = useState(null);
  const { isLoading: loading, data: queryData } = useQuery({
    queryKey: ['project-staff', projectId],
    queryFn: async () => {
      const [staffRes, crewRes] = await Promise.all([
        api.get(`projects/api/project-staff/?project_id=${projectId}`),
        api.get('crew/api/crew/'),
      ]);
      setStaff(staffRes);
      setCrewMembers(crewRes);
      return { staff: staffRes, crew: crewRes };
    },
  });

  useEffect(() => {
    if (!queryData) return;
    setStaff(queryData.staff || []);
    setCrewMembers(queryData.crew || []);
  }, [queryData]);

  const fetchStaff = useCallback((silent = false) => {
    if (!silent) queryClient.invalidateQueries({ queryKey: ['project-staff', projectId] });
    else queryClient.invalidateQueries({ queryKey: ['project-staff', projectId] });
  }, [queryClient, projectId]);

  const [editingMemberId, setEditingMemberId] = useState(null);
  const [rowFormData, setRowFormData] = useState({ role: '', rate_type: 'hourly', rate: 0, time_worked: 0, date_from: '', date_to: '' });
  const [pendingChanges, setPendingChanges] = useState(new Map());

  // ── Staff Groups state ───────────────────────────────────────────────────
  const [staffGroups, setStaffGroups] = useState([]);
  const [editingGroupId, setEditingGroupId] = useState(null); // null | 'new' | <id>
  const [groupFormData, setGroupFormData] = useState({ label: '', count: 1, rate_type: 'daily', rate: 0, time_worked: 0 });
  const [pendingGroupChanges, setPendingGroupChanges] = useState(new Map()); // key: id or 'new_<tempKey>'
  const [groupError, setGroupError] = useState(null);

  const fetchGroups = useCallback(() => {
    api.get(`projects/api/project-staff-groups/?project_id=${projectId}`)
      .then(res => setStaffGroups(res))
      .catch(() => {});
  }, [projectId]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const hasGroupChanges = pendingGroupChanges.size > 0;
  const hasAnyUnsaved = pendingChanges.size > 0 || hasGroupChanges;
  useEffect(() => { isDirtyRef.current = hasAnyUnsaved; }, [hasAnyUnsaved]);

  // Report dirty state to parent (ProjectDetail handles the blocker)
  useEffect(() => { onDirtyChange?.(hasAnyUnsaved); }, [hasAnyUnsaved]);

  const [rowError, setRowError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useSortPreference('project-staff', { key: 'name', direction: 'asc' });
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };
  const rateTypeOptions = [
    { value: 'all', label: t('projects.staff.allRateTypes') },
    { value: 'hourly', label: t('projects.staff.hourly') },
    { value: 'daily', label: t('projects.staff.daily') },
  ];
  const [selectedRateType, setSelectedRateType] = useState({ value: 'all' });
  const selectedRateTypeOption = rateTypeOptions.find(o => o.value === selectedRateType.value) || rateTypeOptions[0];
  const getCrewMemberId = member => Number(member.id);
  const getStaffCrewMemberId = entry => Number(
    entry.crew_member_detail?.id
      ?? entry.crew_member_id
      ?? (typeof entry.crew_member === 'object' ? entry.crew_member.id : entry.crew_member),
  );

  const staffRows = useMemo(() => {
    const uniqueCrewMembers = Array.from(
      new Map(crewMembers.map(member => [getCrewMemberId(member), member])).values(),
    );
    const rows = uniqueCrewMembers
      .flatMap(member => {
        const crewMemberId = getCrewMemberId(member);
        const entries = staff.filter(s => getStaffCrewMemberId(s) === crewMemberId);
        const activeEntries = entries.filter(entry => !pendingChanges.get(`${crewMemberId}:${entry.id}`)?.isDeleted);
        return activeEntries.length
          ? activeEntries.map(entry => ({ ...member, crewMemberId, projectStaff: entry, rowKey: `${crewMemberId}:${entry.id}` }))
          : [{ ...member, crewMemberId, projectStaff: null, rowKey: `${crewMemberId}:empty` }];
      });

    pendingChanges.forEach((change, rowKey) => {
      if (!change.isNew || change.isDeleted || rows.some(row => row.rowKey === rowKey)) return;
      const member = uniqueCrewMembers.find(item => getCrewMemberId(item) === Number(change.memberId));
      if (member) rows.push({ ...member, crewMemberId: getCrewMemberId(member), projectStaff: null, rowKey });
    });

    const assignedRows = rows.filter(row => row.projectStaff);
    const getPersonName = member => (
      member.display_name
        || `${member.user?.first_name || ''} ${member.user?.last_name || ''}`.trim()
        || member.user?.username
        || ''
    ).trim().toLocaleLowerCase();
    const hasSamePersonAssigned = row => assignedRows.some(assigned => {
      if (assigned.crewMemberId === row.crewMemberId) return true;
      if (getPersonName(assigned) !== getPersonName(row)) return false;
      return true;
    });

    return rows.filter(member => {
      const pendingEntry = pendingChanges.get(member.rowKey);
      const isPendingRole = pendingEntry?.isNew && !pendingEntry?.isDeleted;
      if (!member.projectStaff && !isPendingRole && hasSamePersonAssigned(member)) return false;
        const name = member.display_name
          || `${member.user?.first_name || ''} ${member.user?.last_name || ''}`.trim()
          || member.user?.username || '';
        if (searchQuery && !name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        if (selectedRateType?.value !== 'all' && member.projectStaff) {
          if (member.projectStaff.rate_type !== selectedRateType.value) return false;
        }
        return true;
      });
  }, [crewMembers, staff, pendingChanges, searchQuery, selectedRateType]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return staffRows;
    return [...staffRows].sort((a, b) => {
      const aPS = pendingChanges.get(a.rowKey) || a.projectStaff;
      const bPS = pendingChanges.get(b.rowKey) || b.projectStaff;
      const name = m => m.display_name || `${m.user?.first_name || ''} ${m.user?.last_name || ''}`.trim() || m.user?.username || '';
      const memberOrder = name(a).localeCompare(name(b), 'cs', { sensitivity: 'base' })
        || a.crewMemberId - b.crewMemberId;
      if (memberOrder !== 0) return memberOrder;
      let aVal, bVal;
      switch (sortConfig.key) {
        case 'name':       aVal = name(a); bVal = name(b); break;
        case 'role':       aVal = aPS?.role || ''; bVal = bPS?.role || ''; break;
        case 'rate_type':  aVal = aPS?.rate_type || ''; bVal = bPS?.rate_type || ''; break;
        case 'rate':       aVal = parseFloat(aPS?.rate || 0); bVal = parseFloat(bPS?.rate || 0); break;
        case 'time_worked':aVal = parseFloat(aPS?.time_worked || 0); bVal = parseFloat(bPS?.time_worked || 0); break;
        case 'total':
          aVal = aPS ? parseFloat(aPS.rate || 0) * parseFloat(aPS.time_worked || 0) : 0;
          bVal = bPS ? parseFloat(bPS.rate || 0) * parseFloat(bPS.time_worked || 0) : 0;
          break;
        default: aVal = ''; bVal = '';
      }
      if (typeof aVal === 'string') {
        const cmp = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [staffRows, sortConfig, pendingChanges]);

  const editingRow = editingMemberId ? sortedRows.find(r => r.rowKey === editingMemberId) : null;
  const conflictResult = useConflictCheck('crew', {
    crew_member: rowFormData.crew_member,
    date_from: rowFormData.date_from || projectStartDate,
    date_to: rowFormData.date_to || projectEndDate,
    exclude_staff_id: editingRow?.projectStaff?.id,
  }, !!editingMemberId);

  // ── realtime updates ─────────────────────────────────────────────────────
  const isDirtyRef = useRef(false);
  const _staffDebounce = useRef(null);

  useRealtimeEvents(useCallback((e) => {
    if (e.model !== 'projects.projectstaff' && e.model !== 'crew_members.crewmember') return;
    if (isDirtyRef.current) return; // user has unsaved edits — skip
    clearTimeout(_staffDebounce.current);
    _staffDebounce.current = setTimeout(() => fetchStaff(true), 400);
  }, [fetchStaff])); // eslint-disable-line react-hooks/exhaustive-deps

  const crewLoading = loading;

  const handleStartAdd = (member) => {
    const hasRole = member.projectStaff || pendingChanges.get(member.rowKey)?.isNew;
    const rowKey = hasRole
      ? `${member.crewMemberId}:new:${Date.now()}`
      : `${member.crewMemberId}:empty`;
    setEditingMemberId(rowKey);
    const initialRateType = 'hourly';
    setRowFormData({
      project: projectId,
      crew_member: member.id,
      role: member.profession?.name || '',
      show_contact_in_offer: false,
      rate_type: initialRateType,
      rate: parseFloat(member.default_hourly_rate || 0),
      time_worked: 0,
      date_from: projectStartDate || '',
      date_to: projectEndDate || '',
    });
    setPendingChanges(prev => {
      const next = new Map(prev);
      next.set(rowKey, {
        project: projectId, crew_member: member.id, memberId: member.id,
        role: member.profession?.name || '', show_contact_in_offer: false, rate_type: initialRateType,
        rate: parseFloat(member.default_hourly_rate || 0), time_worked: 0,
        date_from: projectStartDate || '', date_to: projectEndDate || '',
        isNew: true, isDeleted: false,
      });
      return next;
    });
  };

  const handleStartEdit = (member) => {
    const pending = pendingChanges.get(member.rowKey);
    const source = (pending && !pending.isDeleted) ? pending : member.projectStaff;
    setEditingMemberId(member.rowKey);
    setRowFormData({
      project: projectId,
      crew_member: member.id,
      role: source?.role || '',
      show_contact_in_offer: source?.show_contact_in_offer || false,
      rate_type: source?.rate_type || 'hourly',
      rate: source?.rate || 0,
      time_worked: source?.time_worked || 0,
      date_from: source?.date_from || '',
      date_to: source?.date_to || '',
    });
  };

  const commitRowToPending = (formData, rowKey) => {
    if (!rowKey) return;
    const row = staffRows.find(item => item.rowKey === rowKey);
    const existingPS = row?.projectStaff;
  const memberId = formData.crew_member || row?.crewMemberId;
    setPendingChanges(prev => {
      const next = new Map(prev);
      next.set(rowKey, {
        ...formData,
        memberId,
        isNew: !existingPS,
        isDeleted: false,
        psId: existingPS?.id,
      });
      return next;
    });
  };

  const handleContactToggle = (member) => {
    const source = pendingChanges.get(member.rowKey) || member.projectStaff;
    if (!source) return;
    commitRowToPending(
      { ...source, show_contact_in_offer: !source.show_contact_in_offer, crew_member: member.id },
      member.rowKey,
    );
  };

  const exitEdit = () => {
    setEditingMemberId(null);
    setRowFormData({ role: '', rate_type: 'hourly', rate: 0, time_worked: 0, date_from: '', date_to: '' });
    setRowError(null);
  };

  const handleCancelEdit = () => exitEdit();

  const handleRowFormChange = (e) => {
    const { name, value } = e.target;
    setRowFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name !== 'role' || next.role.trim()) commitRowToPending(next, editingMemberId);
      return next;
    });
  };

  const handleSaveAll = async () => {
    try {
      // Fetch fresh server state first to avoid last-write-wins overwrite
      const freshStaff = await api.get(`projects/api/project-staff/?project_id=${projectId}`);

      const promises = [];
      pendingChanges.forEach((change, rowKey) => {
        const memberId = change.memberId || rowKey.split(':')[0];
        // Re-derive server record from fresh data
        const serverRecord = change.psId
          ? freshStaff.find(s => s.id === change.psId)
          : null;
        if (change.isDeleted) {
          const psId = serverRecord?.id ?? change.psId;
          if (psId) promises.push(api.delete(`projects/api/project-staff/${psId}/`));
        } else if (change.isNew || !serverRecord) {
          promises.push(api.post('projects/api/project-staff/', {
            project: projectId, crew_member: memberId,
            role: change.role, rate_type: change.rate_type,
            show_contact_in_offer: change.show_contact_in_offer || false,
            rate: change.rate, time_worked: change.time_worked,
            date_from: change.date_from || null, date_to: change.date_to || null,
          }));
        } else {
          promises.push(api.put(`projects/api/project-staff/${serverRecord.id}/`, {
            project: projectId, crew_member: memberId,
            role: change.role, rate_type: change.rate_type,
            show_contact_in_offer: change.show_contact_in_offer || false,
            rate: change.rate, time_worked: change.time_worked,
            date_from: change.date_from || null, date_to: change.date_to || null,
          }));
        }
      });
      await Promise.all(promises);
      setPendingChanges(new Map());

      // Save staff group changes
      const groupPromises = [];
      pendingGroupChanges.forEach((change, key) => {
        if (change.isDeleted) {
          if (change.id) groupPromises.push(api.delete(`projects/api/project-staff-groups/${change.id}/`));
        } else if (change.isNew) {
          groupPromises.push(api.post('projects/api/project-staff-groups/', {
            project: projectId, label: change.label, count: change.count,
            rate_type: change.rate_type, rate: change.rate, time_worked: change.time_worked,
          }));
        } else {
          groupPromises.push(api.put(`projects/api/project-staff-groups/${change.id}/`, {
            project: projectId, label: change.label, count: change.count,
            rate_type: change.rate_type, rate: change.rate, time_worked: change.time_worked,
          }));
        }
      });
      await Promise.all(groupPromises);
      setPendingGroupChanges(new Map());
      setEditingGroupId(null);

      fetchStaff();
      fetchGroups();
    } catch (err) {
      console.error('Error saving all changes:', err);
      setError(t('projects.staff.saveError'));
    }
  };

  const handleCancelAll = () => {
    setPendingChanges(new Map());
    setPendingGroupChanges(new Map());
    setEditingGroupId(null);
    exitEdit();
  };

  const handleDelete = async (staffId, rowKey) => {
    if (await confirm(t('projects.staff.confirmDelete'))) {
      setPendingChanges(prev => {
        const next = new Map(prev);
        if (!staffId) {
          next.delete(rowKey);
        } else {
          next.set(rowKey, { isDeleted: true, psId: staffId });
        }
        return next;
      });
    }
  };

  const handleDeleteAllForMember = async (member, memberRows) => {
    if (!await confirm(t('projects.staff.confirmDeleteAll', 'Opravdu chcete odebrat všechny role tohoto člověka?'))) return;

    setPendingChanges(prev => {
      const next = new Map(prev);
      memberRows.forEach(row => {
        const pending = next.get(row.rowKey);
        const staffId = row.projectStaff?.id || pending?.psId;
        if (staffId) {
          next.set(row.rowKey, { ...(pending || row.projectStaff), isDeleted: true, psId: staffId });
        } else {
          next.delete(row.rowKey);
        }
      });
      return next;
    });
    exitEdit();
  };

  if (loading || crewLoading) return <CenteredLoader />;

  const sortIcon = (key) => (
    sortConfig.key === key
      ? <i className={`ti ti-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
      : <i className="ti ti-selector ms-1 opacity-25"></i>
  );

  return (
    <PageSection
      title={t('projects.staff.title')}
      description={t('projects.staff.description')}
      bodyClassName="p-0"
    >
      <div className="px-3 pt-3">
        <div className="mb-3 row">
          <div className="col-md-8 mb-2 mb-md-0">
            <InputGroup>
              <Form.Control
                placeholder={t('projects.staff.searchPlaceholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </div>
          <div className="col-md-4">
            <Select
              value={selectedRateTypeOption}
              onChange={setSelectedRateType}
              options={rateTypeOptions}
              placeholder={t('projects.staff.filterByRateType')}
              theme={selectTheme}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
        </div>

        {error && <Alert variant="danger" className="app-inline-alert">{error}</Alert>}

        {hasAnyUnsaved && (
          <Alert variant="warning" className="app-inline-alert mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>{pendingChanges.size + pendingGroupChanges.size}</strong> {t('projects.staff.unsavedChanges')}
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" size="sm" onClick={handleCancelAll}>
                  {t('common.cancel')}
                </Button>
                <Button variant="warning" size="sm" onClick={handleSaveAll} className="fw-medium">
                  <i className="ti ti-check me-1"></i>
                  {t('common.save')} ({pendingChanges.size + pendingGroupChanges.size})
                </Button>
              </div>
            </div>
          </Alert>
        )}
        {sortedRows.length === 0 ? <Alert variant="info" className="app-inline-alert mb-3">{t('projects.staff.noStaff')}</Alert> : null}
      </div>

      {sortedRows.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover table-vcenter mb-0">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('name')}>
                    {t('projects.staff.name')}{sortIcon('name')}
                  </th>
                  <th style={{ whiteSpace: 'nowrap' }}>{t('projects.staff.showContact', 'Zobrazit kontakt')}</th>
                  <th className="d-none d-md-table-cell" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('role')}>
                    {t('projects.staff.role')}{sortIcon('role')}
                  </th>
                  <th className="d-none d-xl-table-cell" style={{ whiteSpace: 'nowrap' }}>
                    {t('projects.staff.dateRange', 'Termín')}
                  </th>
                  <th style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('rate_type')}>
                    {t('projects.staff.rateType')}{sortIcon('rate_type')}
                  </th>
                  <th className="text-end" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('rate')}>
                    {t('projects.staff.rate')}{sortIcon('rate')}
                  </th>
                  <th className="text-end" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('time_worked')}>
                    {t('projects.staff.timeWorked')}{sortIcon('time_worked')}
                  </th>
                  <th className="text-end" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('total')}>
                    {t('projects.staff.total')}{sortIcon('total')}
                  </th>
                  <th className="text-center">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((member, rowIndex) => {
                  const pendingEntry = pendingChanges.get(member.rowKey);
                  const hasPending = !!pendingEntry && !pendingEntry?.isDeleted;
                  const isMarkedForDeletion = pendingEntry?.isDeleted === true;
                  const isEditing = editingMemberId === member.rowKey;
                  const inProject = (!!member.projectStaff && !isMarkedForDeletion) || hasPending;
                  const ps = hasPending ? pendingEntry : member.projectStaff;
                  const displayName = member.display_name
                    || `${member.user?.first_name || ''} ${member.user?.last_name || ''}`.trim()
                    || member.user?.username;
                  const isFirstRole = sortedRows.findIndex(row => row.crewMemberId === member.crewMemberId) === rowIndex;
                  const memberRows = sortedRows.filter(row => row.crewMemberId === member.crewMemberId);
                  const isGroupEditing = memberRows.some(row => row.rowKey === editingMemberId);
                  const memberEntries = sortedRows
                    .filter(row => row.crewMemberId === member.crewMemberId)
                    .map(row => pendingChanges.get(row.rowKey) || row.projectStaff)
                    .filter(entry => entry && !pendingChanges.get(`${member.id}:${entry.id}`)?.isDeleted);
                  const hasMultipleRoles = memberEntries.length > 1;
                  const isGroupedRole = inProject;
                  const isFirstGroupedRole = isGroupedRole && memberRows[0]?.rowKey === member.rowKey;
                  const isLastGroupedRole = isGroupedRole && memberRows[memberRows.length - 1]?.rowKey === member.rowKey;
                  const summaryHourlyTime = memberEntries
                    .filter(entry => entry.rate_type !== 'daily')
                    .reduce((sum, entry) => sum + parseFloat(entry.time_worked || 0), 0);
                  const summaryDailyTime = memberEntries
                    .filter(entry => entry.rate_type === 'daily')
                    .reduce((sum, entry) => sum + parseFloat(entry.time_worked || 0), 0);
                  const summaryTime = [
                    summaryHourlyTime > 0 ? `${summaryHourlyTime.toFixed(2)} h` : '',
                    summaryDailyTime > 0 ? `${summaryDailyTime.toFixed(2)} d` : '',
                  ].filter(Boolean).join(' + ');
                  const summaryTotal = memberEntries.reduce(
                    (sum, entry) => sum + parseFloat(entry.rate || 0) * parseFloat(entry.time_worked || 0),
                    0,
                  );

                  return (
                    <React.Fragment key={member.rowKey}>
                    {isFirstRole && inProject && (
                      <tr
                        className={`project-staff-summary-row ${isGroupEditing ? 'is-group-editing' : ''}`}
                        onClick={event => {
                          if (event.target.closest('button') || event.target.closest('input')) return;
                          const firstRole = memberRows.find(row => row.projectStaff || pendingChanges.get(row.rowKey)?.isNew);
                          if (firstRole && canManage) handleStartEdit(firstRole);
                        }}
                        style={{ cursor: canManage ? 'pointer' : 'default' }}
                      >
                        <td>
                          <div className="fw-bold">{displayName}</div>
                          <small className="text-muted">
                            {memberEntries.length === 1
                              ? t('projects.staff.roleSummary', '1 role')
                              : t('projects.staff.rolesSummary', 'Souhrn {{count}} rolí', { count: memberEntries.length })}
                          </small>
                        </td>
                        <td />
                        <td className="fw-medium">{t('projects.staff.totalForPerson', 'Celkem za osobu')}</td>
                        <td><span className="text-muted">-</span></td>
                        <td className="text-end"><span className="text-muted">-</span></td>
                        <td className="text-end"><span className="text-muted">-</span></td>
                        <td className="text-end fw-medium">{summaryTime || '-'}</td>
                        <td className="text-end fw-bold" style={{ color: 'var(--tblr-primary)' }}>{formatCurrency(summaryTotal)}</td>
                        <td className="text-center">
                          {canManage && (
                            <div className="btn-group">
                              <Button size="sm" variant="outline-success" onClick={() => handleStartAdd(member)} title={t('projects.staff.add')}>
                                <i className="ti ti-plus"></i>
                              </Button>
                              <Button size="sm" variant="outline-danger" onClick={() => handleDeleteAllForMember(member, memberRows)} title={t('projects.staff.removeAll', 'Odebrat všechny role')}>
                                <i className="ti ti-trash"></i>
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    <tr
                      className={`${isGroupedRole || inProject ? 'project-staff-role-row' : ''} ${isGroupedRole ? 'is-first-role is-last-role' : inProject ? 'is-single-role' : ''} ${isEditing ? 'is-editing table-warning' : ''} ${hasPending && !isEditing ? 'border-warning border-2' : ''} ${isMarkedForDeletion ? 'opacity-50' : ''}`}
                      onClick={(e) => {
                        if (e.target.closest('button') || e.target.closest('input') || e.target.closest('select')) return;
                          if (isMarkedForDeletion || !canManage) return;
                          if (isEditing) { exitEdit(); return; }
                          if (editingMemberId) exitEdit();
                        if (inProject) {
                          handleStartEdit(member);
                        } else {
                          handleStartAdd(member);
                        }
                      }}
                      style={{
                        cursor: !isEditing && !isMarkedForDeletion && canManage ? 'pointer' : 'default',
                      }}
                    >
                      <td>
                        {!isGroupedRole && <div className="fw-medium">{displayName}</div>}
                        {isGroupedRole && inProject && (
                          <small className="text-muted d-md-none ps-2">
                            <span className="me-1" aria-hidden="true">-&gt;</span>{ps.role}
                          </small>
                        )}
                      </td>
                      <td>
                        <Form.Check
                          type="checkbox"
                          id={`show-contact-${member.rowKey}`}
                          label={t('projects.staff.showContactShort', 'Zobrazit')}
                          checked={!!ps?.show_contact_in_offer}
                          disabled={!inProject || !canManage || isMarkedForDeletion}
                          onChange={() => handleContactToggle(member)}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      {isEditing ? (
                        <>
                          <td>
                            <Form.Control
                              size="sm" type="text" name="role"
                              value={rowFormData.role}
                              onChange={e => { setRowError(null); handleRowFormChange(e); }}
                              placeholder={t('projects.staff.role')}
                              isInvalid={!!rowError}
                            />
                            {rowError && <div className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }}>{rowError}</div>}
                          </td>
                          <td className="d-none d-xl-table-cell">
                            <DateRangeField
                              dateFrom={rowFormData.date_from}
                              dateTo={rowFormData.date_to}
                              projectStartDate={projectStartDate}
                              projectEndDate={projectEndDate}
                              onChange={({ dateFrom, dateTo }) => setRowFormData(prev => {
                                const next = { ...prev, date_from: dateFrom || '', date_to: dateTo || '' };
                                commitRowToPending(next, editingMemberId);
                                return next;
                              })}
                              warning={conflictResult?.count > 0 && (
                                <div className="text-warning small">
                                  <small>⚠ {t('projects.staff.conflictWarning', { count: conflictResult.count, defaultValue: 'Konflikt s {{count}} jiným projektem' })}</small>
                                </div>
                              )}
                            />
                          </td>
                          <td>
                            <Form.Select size="sm" name="rate_type" value={rowFormData.rate_type} onChange={e => {
                              const newType = e.target.value;
                              const defaultRate = newType === 'daily'
                                ? parseFloat(member.default_daily_rate || 0)
                                : parseFloat(member.default_hourly_rate || 0);
                              setRowFormData(prev => {
                                const next = { ...prev, rate_type: newType, rate: defaultRate };
                                commitRowToPending(next, editingMemberId);
                                return next;
                              });
                            }}>
                              <option value="hourly">{t('projects.staff.hourly')}</option>
                              <option value="daily">{t('projects.staff.daily')}</option>
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control size="sm" type="number" name="rate" value={rowFormData.rate} onChange={handleRowFormChange} step="any" min="0" />
                          </td>
                          <td>
                            <Form.Control size="sm" type="number" name="time_worked" value={rowFormData.time_worked} onChange={handleRowFormChange} step="any" min="0" autoFocus />
                          </td>
                          <td className="text-end fw-semibold" style={{ color: 'var(--tblr-primary)' }}>
                            {formatCurrency(parseFloat(rowFormData.rate || 0) * parseFloat(rowFormData.time_worked || 0))}
                          </td>
                          <td className="text-center">
                            <Button size="sm" variant="outline-secondary" onClick={exitEdit} title={t('common.cancel')}>
                              <i className="ti ti-x"></i>
                            </Button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="d-none d-md-table-cell">
                            {inProject ? (
                              <span className={isGroupedRole ? 'project-staff-role-label' : undefined}>
                                {ps.role}
                              </span>
                            ) : <span className="text-muted">-</span>}
                          </td>
                          <td className="d-none d-xl-table-cell">
                            {inProject ? (
                              (ps.date_from || ps.date_to)
                                ? <small>{ps.date_from || '…'} – {ps.date_to || '…'}</small>
                                : <small className="text-muted">{t('projects.staff.wholeProject', 'Celý projekt')}</small>
                            ) : <span className="text-muted">-</span>}
                          </td>
                          <td>
                            {inProject && (
                              <Badge bg="green-lt" text="green" style={{ fontWeight: 500 }}>
                                {ps.rate_type === 'hourly' ? t('projects.staff.hourly') : t('projects.staff.daily')}
                              </Badge>
                            )}
                          </td>
                          <td className="text-end">
                            {inProject ? formatCurrency(ps.rate) : <span className="text-muted">-</span>}
                          </td>
                          <td className="text-end">
                            {inProject
                              ? <>{ps.time_worked}<small className="text-muted ms-1">{ps.rate_type === 'hourly' ? t('projects.staff.hours') : t('projects.staff.days')}</small></>
                              : <span className="text-muted">-</span>}
                          </td>
                          <td className="text-end fw-semibold" style={{ color: inProject ? 'var(--tblr-primary)' : undefined }}>
                            {inProject ? formatCurrency(ps.rate * ps.time_worked) : <span className="text-muted">-</span>}
                          </td>
                          <td className="text-center">
                            {canManage && (
                              inProject ? (
                                <div className="btn-group">
                                  <Button size="sm" variant="outline-info" onClick={() => handleStartEdit(member)} title={t('common.edit')}>
                                    <i className="ti ti-edit"></i>
                                  </Button>
                                  <Button size="sm" variant="outline-danger" onClick={() => handleDelete(member.projectStaff?.id, member.rowKey)} title={t('projects.staff.remove')}>
                                    <i className="ti ti-trash"></i>
                                  </Button>
                                </div>
                              ) : (
                                <Button size="sm" variant="outline-success" onClick={() => handleStartAdd(member)} title={t('projects.staff.add')}>
                                  <i className="ti ti-plus"></i>
                                </Button>
                              )
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
              {staff.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan="6" className="text-end text-muted fw-medium">{t('projects.staff.total')}</td>
                    <td className="text-end fw-bold" style={{ color: 'var(--tblr-primary)' }}>
                      {formatCurrency(staff.reduce((sum, m) => sum + parseFloat(m.rate || 0) * parseFloat(m.time_worked || 0), 0))}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
          </table>
        </div>
      ) : null}

      {/* ── Skupiny pracovníků ──────────────────────────────────────────── */}
      <div className="px-3 pt-3 pb-1">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{t('projects.staffGroups.title', 'Skupiny pracovníků')}</strong>
          {canManage && (
            <Button size="sm" variant="outline-success" onClick={() => {
              setEditingGroupId('new');
              setGroupFormData({ label: '', count: 1, rate_type: 'daily', rate: 0, time_worked: 0 });
              setGroupError(null);
            }}>
              <i className="ti ti-plus me-1"></i>
              {t('projects.staffGroups.add', 'Přidat skupinu')}
            </Button>
          )}
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover table-vcenter mb-0">
          <thead>
            <tr>
              <th>{t('projects.staffGroups.label', 'Popis')}</th>
              <th>{t('projects.staff.rateType', 'Typ sazby')}</th>
              <th className="text-end">{t('projects.staffGroups.count', 'Počet lidí')}</th>
              <th className="text-end">{t('projects.staffGroups.ratePerPerson', 'Sazba/os.')}</th>
              <th className="text-end">{t('projects.staff.timeWorked', 'Odpracováno')}</th>
              <th className="text-end">{t('projects.staff.total', 'Celkem')}</th>
              <th className="text-center">{t('common.actions', 'Akce')}</th>
            </tr>
          </thead>
          <tbody>
            {staffGroups.filter(sg => !pendingGroupChanges.get(sg.id)?.isDeleted).map(sg => {
              const pending = pendingGroupChanges.get(sg.id);
              const display = pending || sg;
              const isEditing = editingGroupId === sg.id;
              const total = parseFloat(display.count || 0) * parseFloat(display.rate || 0) * parseFloat(display.time_worked || 0);
              return (
                <tr key={sg.id}
                  className={isEditing ? 'table-warning' : pending ? 'border-warning border-2' : ''}
                  onClick={e => {
                    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('select')) return;
                    if (!canManage) return;
                    if (isEditing) { setEditingGroupId(null); return; }
                    setEditingGroupId(sg.id);
                    setGroupFormData({ label: sg.label, count: sg.count, rate_type: sg.rate_type, rate: sg.rate, time_worked: sg.time_worked });
                    setGroupError(null);
                  }}
                  style={{ cursor: canManage ? 'pointer' : 'default' }}
                >
                  {isEditing ? (
                    <>
                      <td>
                        <Form.Control size="sm" type="text" value={groupFormData.label}
                          onChange={e => { setGroupError(null); setGroupFormData(p => ({ ...p, label: e.target.value })); }}
                          placeholder={t('projects.staffGroups.labelPlaceholder', 'Zvukaři, Bedňáci...')}
                          isInvalid={!!groupError} />
                        {groupError && <div className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }}>{groupError}</div>}
                      </td>
                      <td>
                        <Form.Select size="sm" value={groupFormData.rate_type}
                          onChange={e => setGroupFormData(p => ({ ...p, rate_type: e.target.value }))}>
                          <option value="hourly">{t('projects.staff.hourly', 'Hodinová')}</option>
                          <option value="daily">{t('projects.staff.daily', 'Denní')}</option>
                        </Form.Select>
                      </td>
                      <td><Form.Control size="sm" type="number" value={groupFormData.count} min="1" step="any"
                        onChange={e => setGroupFormData(p => ({ ...p, count: e.target.value }))} /></td>
                      <td><Form.Control size="sm" type="number" value={groupFormData.rate} min="0" step="any"
                        onChange={e => setGroupFormData(p => ({ ...p, rate: e.target.value }))} /></td>
                      <td><Form.Control size="sm" type="number" value={groupFormData.time_worked} min="0" step="any"
                        onChange={e => setGroupFormData(p => ({ ...p, time_worked: e.target.value }))}
                        autoFocus /></td>
                      <td className="text-end fw-semibold" style={{ color: 'var(--tblr-primary)' }}>
                        {formatCurrency(parseFloat(groupFormData.count || 0) * parseFloat(groupFormData.rate || 0) * parseFloat(groupFormData.time_worked || 0))}
                      </td>
                      <td className="text-center">
                        <div className="btn-group">
                          <Button size="sm" variant="outline-success" title={t('common.confirm', 'Potvrdit')} onClick={() => {
                            if (!groupFormData.label.trim()) { setGroupError(t('projects.staffGroups.labelRequired', 'Popis je povinný')); return; }
                            setPendingGroupChanges(prev => {
                              const next = new Map(prev);
                              next.set(sg.id, { ...groupFormData, id: sg.id, isNew: false, isDeleted: false });
                              return next;
                            });
                            setEditingGroupId(null);
                          }}><i className="ti ti-check"></i></Button>
                          <Button size="sm" variant="outline-secondary" title={t('common.cancel')} onClick={() => setEditingGroupId(null)}>
                            <i className="ti ti-x"></i>
                          </Button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="fw-medium">{display.label}</td>
                      <td>
                        <Badge bg="blue-lt" text="blue" style={{ fontWeight: 500 }}>
                          {display.rate_type === 'hourly' ? t('projects.staff.hourly', 'Hodinová') : t('projects.staff.daily', 'Denní')}
                        </Badge>
                      </td>
                      <td className="text-end">{display.count}</td>
                      <td className="text-end">{formatCurrency(display.rate)}</td>
                      <td className="text-end">
                        {display.time_worked}
                        <small className="text-muted ms-1">
                          {display.rate_type === 'hourly' ? t('projects.staff.hours', 'h') : t('projects.staff.days', 'd')}
                        </small>
                      </td>
                      <td className="text-end fw-semibold" style={{ color: 'var(--tblr-primary)' }}>
                        {formatCurrency(total)}
                      </td>
                      <td className="text-center">
                        {canManage && (
                          <div className="btn-group">
                            <Button size="sm" variant="outline-info" title={t('common.edit')} onClick={() => {
                              setEditingGroupId(sg.id);
                              setGroupFormData({ label: sg.label, count: sg.count, rate_type: sg.rate_type, rate: sg.rate, time_worked: sg.time_worked });
                              setGroupError(null);
                            }}><i className="ti ti-edit"></i></Button>
                            <Button size="sm" variant="outline-danger" title={t('common.delete', 'Smazat')} onClick={async () => {
                              if (await confirm(t('projects.staffGroups.confirmDelete', 'Smazat skupinu?'))) {
                                setPendingGroupChanges(prev => {
                                  const next = new Map(prev);
                                  next.set(sg.id, { id: sg.id, isDeleted: true });
                                  return next;
                                });
                              }
                            }}><i className="ti ti-trash"></i></Button>
                          </div>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
            {/* New group row */}
            {editingGroupId === 'new' && (
              <tr className="table-warning">
                <td>
                  <Form.Control size="sm" type="text" value={groupFormData.label} autoFocus
                    onChange={e => { setGroupError(null); setGroupFormData(p => ({ ...p, label: e.target.value })); }}
                    placeholder={t('projects.staffGroups.labelPlaceholder', 'Zvukaři, Bedňáci...')}
                    isInvalid={!!groupError} />
                  {groupError && <div className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }}>{groupError}</div>}
                </td>
                <td>
                  <Form.Select size="sm" value={groupFormData.rate_type}
                    onChange={e => setGroupFormData(p => ({ ...p, rate_type: e.target.value }))}>
                    <option value="hourly">{t('projects.staff.hourly', 'Hodinová')}</option>
                    <option value="daily">{t('projects.staff.daily', 'Denní')}</option>
                  </Form.Select>
                </td>
                <td><Form.Control size="sm" type="number" value={groupFormData.count} min="1" step="any"
                  onChange={e => setGroupFormData(p => ({ ...p, count: e.target.value }))} /></td>
                <td><Form.Control size="sm" type="number" value={groupFormData.rate} min="0" step="any"
                  onChange={e => setGroupFormData(p => ({ ...p, rate: e.target.value }))} /></td>
                <td><Form.Control size="sm" type="number" value={groupFormData.time_worked} min="0" step="any"
                  onChange={e => setGroupFormData(p => ({ ...p, time_worked: e.target.value }))} /></td>
                <td className="text-end fw-semibold" style={{ color: 'var(--tblr-primary)' }}>
                  {formatCurrency(parseFloat(groupFormData.count || 0) * parseFloat(groupFormData.rate || 0) * parseFloat(groupFormData.time_worked || 0))}
                </td>
                <td className="text-center">
                  <div className="btn-group">
                    <Button size="sm" variant="outline-success" title={t('common.confirm', 'Potvrdit')} onClick={() => {
                      if (!groupFormData.label.trim()) { setGroupError(t('projects.staffGroups.labelRequired', 'Popis je povinný')); return; }
                      const tempKey = `new_${Date.now()}`;
                      setPendingGroupChanges(prev => {
                        const next = new Map(prev);
                        next.set(tempKey, { ...groupFormData, id: null, isNew: true, isDeleted: false });
                        return next;
                      });
                      // Show optimistically in list until saved
                      setStaffGroups(prev => [...prev, { id: tempKey, ...groupFormData }]);
                      setEditingGroupId(null);
                    }}><i className="ti ti-check"></i></Button>
                    <Button size="sm" variant="outline-secondary" title={t('common.cancel')} onClick={() => setEditingGroupId(null)}>
                      <i className="ti ti-x"></i>
                    </Button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
          {staffGroups.filter(sg => !pendingGroupChanges.get(sg.id)?.isDeleted).length > 0 && (
            <tfoot>
              <tr>
                <td colSpan="5" className="text-end text-muted fw-medium">{t('projects.staff.total', 'Celkem')}</td>
                <td className="text-end fw-bold" style={{ color: 'var(--tblr-primary)' }}>
                  {formatCurrency(
                    staffGroups
                      .filter(sg => !pendingGroupChanges.get(sg.id)?.isDeleted)
                      .reduce((sum, sg) => {
                        const d = pendingGroupChanges.get(sg.id) || sg;
                        return sum + parseFloat(d.count || 0) * parseFloat(d.rate || 0) * parseFloat(d.time_worked || 0);
                      }, 0)
                  )}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </PageSection>
  );
};

export default ProjectStaff;
